import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PergolaUpgradeCta } from "@/components/PergolaUpgradeCta";
import { Lightbox } from "@/components/Lightbox";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import awningUnderneathAsset from "@/assets/awning-underneath-frame.jpg.asset.json";
import skinCancerSealAsset from "@/assets/skin-cancer-foundation-seal.png.asset.json";
import sunbrellaVideoAsset from "@/assets/sunbrella-outdoor-2026.mp4.asset.json";
import { ArrowRight, Check, Phone, Ruler, Hammer, Scissors, Truck, Wrench, ShieldCheck, Maximize2 } from "lucide-react";
import { useState } from "react";
import shareImage from "@/assets/tuff-shed-awning.jpg";
import blueCanvas from "@/assets/awning-blue-canvas.jpg";
import mainStreetAwning from "@/assets/awning-400-s-main.jpg";
import stripedFabric from "@/assets/awning-striped-fabric.jpg";
import blueStripedFabric from "@/assets/awning-blue-striped-fabric.jpg";
import bradzoilBanner from "@/assets/awning-bradzoil-banner.jpg";
import tuffShedAwning from "@/assets/tuff-shed-awning.jpg";

import backyardMetalCover from "@/assets/backyard-metal-patio-cover.jpg";
import sanMarketStorefront from "@/assets/san-market-green-stripe-awning-night.jpg";
import sanMarketInstall from "@/assets/san-market-awning-install-ladder.jpg";
import sanMarketFrame from "@/assets/san-market-awning-frame-underside.jpg";
import mainDome from "@/assets/400-s-main-dome-awning-shop.jpg";
import homeAwningAsset from "@/assets/home-awning-window.jpg.asset.json";
import customBenderAsset from "@/assets/custom-metal-bender.jpg.asset.json";
import canopyAwningsSanAntonio from "@/assets/awnings/canopy-awnings-in-san-antonio.png.asset.json";

const BENEFITS = [
  { icon: ShieldCheck, title: "Welded Aluminum, Not Prefab", text: "TIG-welded aluminum frames built in our Seguin shop — engineered for Texas wind loads, not snapped together from a kit." },
  { icon: Hammer, title: "Sunbrella & Tempotest Canvas", text: "Premium marine canvas with up to 10-year fade warranty. Holds color and integrity for 8–12+ years in direct Texas sun." },
  { icon: Wrench, title: "One Crew, End to End", text: "The same Texas Made Shade crew patterns, fabricates, and installs your awning — no subcontractors, no finger-pointing." },
  { icon: Truck, title: "Re-Cover Your Existing Frame", text: "If your aluminum frame is structurally sound, we can re-cover it with new canvas at a fraction of full replacement cost." },
];

const STEPS = [
  { icon: Ruler, title: "On-Site Measure & Spec", text: "We come to your property, measure the opening, talk through fabric, color, projection, and mounting — and leave you a free, no-pressure estimate. Or work from measurements you provide." },
  { icon: Hammer, title: "Welded Frame Fabrication", text: "Your frame is MIG-welded or TIG-welded from marine-grade aluminum in our Seguin shop. Brackets, gussets, and mounting points engineered for the install. We weld all metals to include steel & aluminum." },
  { icon: Scissors, title: "In-House Pattern & Sew", text: "Sunbrella or Tempotest canvas is patterned, cut, and sewn on our industrial machines — double-stitched seams with UV-resistant thread." },
  { icon: Truck, title: "Delivery & Install", text: "Our install crew delivers, anchors the frame, stretches the canvas, and walks you through care so you get every year out of it." },
  { icon: ShieldCheck, title: "Warranty & Re-Cover Support", text: "10-year fabric warranty backed by Sunbrella, plus lifetime re-cover support — when the canvas fades in 10+ years, we recover the same frame." },
];

const GALLERY = [
  { src: tuffShedAwning, alt: "Custom welded aluminum storefront awning with Sunbrella canvas installed at a Tuff Shed location by Texas Made Shade", label: "Tuff Shed · Storefront Install" },
  { src: mainStreetAwning, alt: "Custom Sunbrella entry awning over a Seguin storefront fabricated by Texas Made Shade", label: "400 S. Main · Seguin Storefront" },
  { src: homeAwningAsset.url, alt: "Home Awning — custom residential window awning in mocha canvas over a brick home, fabricated and installed by Texas Made Shade", label: "Home Awning · Residential Window" },
  { src: bradzoilBanner, alt: "Hand-painted Bradzoil 10 Minute Oil Change commercial banner awning by Texas Made Shade", label: "Bradzoil · Branded Awning" },
  
  { src: blueCanvas, alt: "Roll of marine-grade blue Sunbrella awning canvas on the cutting table at the Texas Made Shade Seguin shop", label: "Sunbrella Canvas · Cutting Table" },
  { src: stripedFabric, alt: "Black and white striped Sunbrella awning fabric laid out for patterning on the shop floor", label: "Striped Sunbrella · Pattern Layout" },
  { src: blueStripedFabric, alt: "Blue striped Sunbrella awning fabric rolls staged for a custom storefront awning build", label: "Blue Striped Canvas · In Stock" },
  { src: backyardMetalCover, alt: "Custom welded steel-frame metal patio cover installed on a two-story San Antonio area home, shading a flagstone backyard patio", label: "Backyard Metal Patio Cover · San Antonio" },
  { src: sanMarketStorefront, alt: "Custom green and white striped Sunbrella storefront awning installed by Texas Made Shade over The San Market gifts and local goods storefront in downtown San Marcos, Texas", label: "The San Market · Downtown San Marcos" },
  { src: sanMarketInstall, alt: "Texas Made Shade installer on a ladder fitting a custom green and white striped canvas awning to a welded aluminum frame at The San Market storefront in downtown San Marcos at night", label: "San Market · Canvas Install" },
  { src: sanMarketFrame, alt: "Underside view of a Texas Made Shade welded aluminum awning frame with green and white striped Sunbrella canvas stretched tight over a downtown San Marcos storefront", label: "Welded Aluminum Frame · Underside" },
  { src: mainDome, alt: "Custom grey Sunbrella half-dome entry awning with hand-painted black '400 S. Main' lettering, fabricated in the Texas Made Shade Seguin canvas shop before install", label: "400 S. Main · Custom Dome Awning" },
];

const WIDTH_OPTIONS = [
  "Under 6 ft",
  "6 – 8 ft",
  "8 – 10 ft",
  "10 – 12 ft",
  "12 – 16 ft",
  "16 – 20 ft",
  "20 – 30 ft",
  "30 ft or wider",
  "Not sure — approximate is fine",
];

const HEIGHT_OPTIONS = [
  "Under 7 ft",
  "7 – 8 ft",
  "8 – 9 ft",
  "9 – 10 ft",
  "10 – 12 ft",
  "12 – 14 ft",
  "14 ft or taller",
  "Not sure — approximate is fine",
];

const PROJECTION_OPTIONS = [
  "Under 3 ft",
  "3 – 4 ft",
  "4 – 6 ft",
  "6 – 8 ft",
  "8 – 10 ft",
  "10 – 12 ft",
  "12 ft or more",
  "Not sure — approximate is fine",
];

const SERIES_OPTIONS = [
  { value: "Alamo Series — Residential", label: "Alamo Series™ — Residential" },
  { value: "Longhorn Series — Commercial", label: "Longhorn Series™ — Commercial" },
  { value: "Rio Grande Series — Hospitality (Hotels & Resorts)", label: "Rio Grande Series™ — Hospitality (Hotels & Resorts)" },
  { value: "Republic Series — Government / Schools", label: "Republic Series™ — Government / Schools" },
];

const FABRIC_OPTIONS = [
  "Sunbrella®",
  "Tempotest® Shade",
  "Serge Ferrari 502 (Waterproof)",
  "Help me decide",
];

function AwningExtras() {
  const selectClass = "w-full border border-input bg-white px-4 py-3 text-sm focus:border-navy-deep focus:outline-none";
  return (
    <div className="space-y-4 border-l-4 border-navy-deep/20 bg-cream/40 p-4">
      <p className="text-xs text-navy-deep/70">
        Approximate sizes are totally fine — this just gets the process started. We'll confirm exact measurements on-site.
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="awningWidth" className="block text-xs font-semibold uppercase tracking-wider text-navy-deep">How Wide?</label>
          <select id="awningWidth" name="awningWidth" defaultValue="" className={`mt-1 ${selectClass}`}>
            <option value="" disabled>Select width…</option>
            {WIDTH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="awningHeight" className="block text-xs font-semibold uppercase tracking-wider text-navy-deep">How Tall in Back?</label>
          <select id="awningHeight" name="awningHeight" defaultValue="" className={`mt-1 ${selectClass}`}>
            <option value="" disabled>Select height…</option>
            {HEIGHT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="awningProjection" className="block text-xs font-semibold uppercase tracking-wider text-navy-deep">How Far Out From Wall?</label>
          <select id="awningProjection" name="awningProjection" defaultValue="" className={`mt-1 ${selectClass}`}>
            <option value="" disabled>Select projection…</option>
            {PROJECTION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      <div className="border border-navy-deep/20 bg-white p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-navy-deep">Photos to attach (below)</div>
        <ul className="mt-2 space-y-1 text-sm text-navy-deep/80">
          <li>• Right-side photo</li>
          <li>• Left-side photo</li>
          <li>• Front-facing photo</li>
          <li>• <strong>For an existing awning re-cover:</strong> a photo of the underneath / frame</li>
        </ul>
        <p className="mt-2 text-xs text-navy-deep/60">Use the "Attach Photos / Files" button further down to upload.</p>
      </div>

      <fieldset className="border border-navy-deep/20 bg-white p-4">
        <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-navy-deep">Which series fits your project?</legend>
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
          {SERIES_OPTIONS.map((s) => (
            <label key={s.value} className="flex items-start gap-2 text-sm text-navy-deep">
              <input type="checkbox" name="awningSeries" value={s.value} className="mt-1 h-4 w-4 border-navy-deep/40 text-navy-deep focus:ring-navy-deep" />
              <span>{s.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label htmlFor="awningFabric" className="block text-xs font-semibold uppercase tracking-wider text-navy-deep">Preferred Fabric</label>
        <select id="awningFabric" name="awningFabric" defaultValue="" className={`mt-1 ${selectClass}`}>
          <option value="" disabled>Select fabric…</option>
          {FABRIC_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    </div>
  );
}



export const Route = createFileRoute("/custom-awnings")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Custom Awnings Texas — Welded Aluminum & Sunbrella Canvas | Texas Made Shade",
      description: "Custom awnings built in Seguin, TX — welded aluminum frames + Sunbrella canvas. See our 5-step build process, benefits, and a project gallery of storefront.",
      image: shareImage,
      imageAlt: "Custom welded aluminum storefront awning with Sunbrella canvas by Texas Made Shade",
      socialTitle: "Custom Awnings · Welded Aluminum + Sunbrella",
      socialDescription: "5-step custom awning process — measure, weld, sew, install, warranty. Built in Seguin, installed across Texas.",
      keywords:
        "custom awnings Texas, welded aluminum awnings, Sunbrella awnings, Tempotest awnings, storefront awnings Texas, commercial awning company, awning re-cover, custom canvas awnings, awning fabrication Seguin, custom awnings San Antonio",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: "Custom Welded Aluminum Awnings with Sunbrella Canvas",
              serviceType: "Custom awning fabrication and installation",
              areaServed: ["Texas", "San Antonio, TX", "Austin, TX", "Houston, TX", "Corpus Christi, TX"],
              provider: {
                "@type": "LocalBusiness",
                name: "Texas Made Shade & Covers",
                telephone: "+1-830-390-9588",
                url: "https://texasmadeshade.com",
              },
              knowsAbout: [
                { "@type": "Service", name: "Storefront & Commercial Awnings" },
                { "@type": "Service", name: "Residential Patio & Window Awnings" },
                { "@type": "Service", name: "Awning Re-Cover (existing frames)" },
              ],

            },
            {
              "@type": "HowTo",
              name: "How Texas Made Shade Builds a Custom Awning",
              step: STEPS.map((s, i) => ({
                "@type": "HowToStep",
                position: i + 1,
                name: s.title,
                text: s.text,
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: CustomAwningsPage,
});

function CustomAwningsPage() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        {/* HERO — single H1 */}
        <section className="relative bg-navy-deep text-white">
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={shareImage}
              alt="Custom welded aluminum storefront awning with Sunbrella canvas installed by Texas Made Shade"
              className="h-full w-full object-cover opacity-30"
              fetchPriority="high"
            />
          </div>
          <div className="relative mx-auto max-w-6xl px-6 py-20 md:py-28">
            <div className="inline-block bg-red px-3 py-1 font-display text-xs tracking-[0.3em] text-white">
              ★ CUSTOM AWNINGS · BUILT IN SEGUIN, TX ★
            </div>
            <h1 className="mt-4 max-w-4xl font-display text-5xl font-bold leading-tight md:text-6xl">
              Custom Welded Aluminum Awnings with Sunbrella Canvas
            </h1>
            <p className="mt-5 max-w-3xl text-lg text-white/85 md:text-xl">
              Storefront, restaurant, patio, and commercial awnings — patterned, fabricated and installed by our own crew across Texas. Every frame is TIG-welded aluminum. Every cover is premium marine canvas. Built to outlast the Texas sun.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/"
                hash="lets-talk"
                className="inline-flex items-center gap-2 bg-red px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-red/90"
              >
                Get a Free Estimate <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="tel:+18303909588"
                className="inline-flex items-center gap-2 border-2 border-white px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
              >
                <Phone className="h-4 w-4" /> Call +1 (830) 390-9588
              </a>
            </div>
          </div>
        </section>

        {/* GALLERY */}
        <section className="bg-cream py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-6">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Recent Projects</div>
            <h2 className="mt-2 max-w-3xl font-display text-4xl font-bold text-navy-deep md:text-5xl">
              Custom Awning Project Gallery
            </h2>
            <p className="mt-4 max-w-3xl text-lg text-navy-deep/80">
              Storefront, restaurant, and commercial awnings built and installed across Texas. Click any image to zoom in.
            </p>
            <ul role="list" className="mt-10 grid list-none gap-5 p-0 sm:grid-cols-2 md:grid-cols-3 lg:gap-6">
              {GALLERY.map((img, i) => (
                <li key={img.src}>
                  <figure className="group relative overflow-hidden border-2 border-navy-deep/10 bg-white shadow-card">
                    <button
                      type="button"
                      onClick={() => setLightboxIndex(i)}
                      aria-label={`${img.label}. Open larger view.`}
                      className="block w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2"
                    >
                      <img
                        src={img.src}
                        alt={img.alt}
                        width={1200}
                        height={900}
                        loading={i < 2 ? "eager" : "lazy"}
                        decoding="async"
                        className="aspect-[4/3] w-full object-cover transition duration-300 group-hover:scale-105"
                      />
                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-navy-deep/0 opacity-0 transition group-hover:bg-navy-deep/40 group-hover:opacity-100">
                        <span className="flex items-center gap-2 bg-white px-4 py-2 font-display text-xs uppercase tracking-wider text-navy-deep">
                          <Maximize2 className="h-4 w-4" /> View
                        </span>
                      </span>
                    </button>
                    <figcaption className="bg-navy-deep px-4 py-2 text-center font-display text-xs uppercase tracking-[0.3em] text-white">
                      {img.label}
                    </figcaption>
                  </figure>
                </li>
              ))}
            </ul>

            {/* CITY / REGION QUICK LINKS */}
            <nav aria-label="Browse awning projects by Texas city" className="mt-10 border-t-2 border-navy-deep/10 pt-8">
              <p className="font-display text-sm font-bold uppercase tracking-[0.2em] text-navy-deep/70">
                Jump to awnings by city:
              </p>
              <ul role="list" className="mt-4 flex flex-wrap gap-2 p-0">
                {[
                  { to: "/san-antonio", label: "San Antonio" },
                  { to: "/austin", label: "Austin" },
                  { to: "/houston", label: "Houston" },
                  { to: "/corpus-christi", label: "Corpus Christi" },
                  { to: "/new-braunfels", label: "New Braunfels" },
                  { to: "/seguin", label: "Seguin" },
                  { to: "/boerne", label: "Boerne" },
                  { to: "/kerrville", label: "Kerrville" },
                ].map((c) => (
                  <li key={c.to}>
                    <Link
                      to={c.to}
                      className="inline-flex items-center gap-1 border-2 border-navy-deep/15 bg-white px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-navy-deep transition hover:border-red hover:bg-red hover:text-white"
                    >
                      {c.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/service-areas"
                    className="inline-flex items-center gap-1 border-2 border-red bg-red px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-red/90 hover:border-red-deep"
                  >
                    Texas Service Areas <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
              </ul>
            </nav>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t-2 border-navy-deep/10 pt-8">
              <p className="font-display text-lg font-bold text-navy-deep">
                See more — by region or service type:
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/awnings" className="inline-flex items-center gap-1 font-display text-sm font-bold uppercase tracking-wider text-red hover:underline">
                  All Awning Options <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/pergola-covers" className="inline-flex items-center gap-1 font-display text-sm font-bold uppercase tracking-wider text-red hover:underline">
                  Gazebo-Pergola Covers Texas <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/projects" className="inline-flex items-center gap-1 font-display text-sm font-bold uppercase tracking-wider text-red hover:underline">
                  Full Project Gallery <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* POST-GALLERY CTA */}
        <section className="bg-red py-14 text-white md:py-16">
          <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-6 text-center md:flex-row md:justify-between md:text-left">
            <div className="md:max-w-2xl">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-white/80">★ Like What You See?</div>
              <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">
                Get a Free Estimate on Your Custom Awning
              </h2>
              <p className="mt-3 text-white/90 md:text-lg">
                Same-business-day response. Send a few photos and rough measurements — we'll spec it, quote it, and build it in our Seguin shop.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/"
                hash="lets-talk"
                className="inline-flex items-center justify-center gap-2 bg-white px-7 py-4 font-display text-base font-extrabold uppercase tracking-wider text-red shadow-bold transition hover:bg-cream"
              >
                Get a Free Estimate <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href="tel:+18303909588"
                data-cta-placement="custom_awnings_post_gallery"
                className="inline-flex items-center justify-center gap-2 border-2 border-white px-7 py-4 font-display text-base font-extrabold uppercase tracking-wider text-white transition hover:bg-white hover:text-red"
              >
                <Phone className="h-5 w-5" /> Call +1 (830) 390-9588
              </a>
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section className="bg-cream py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-6">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Why Choose Our Custom Awnings</div>
            <h2 className="mt-2 max-w-3xl font-display text-4xl font-bold text-navy-deep md:text-5xl">
              Benefits of a Texas Made Shade Awning
            </h2>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex gap-4 border-2 border-navy-deep/10 bg-white p-6 shadow-card">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-red text-white">
                    <b.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-navy-deep">{b.title}</h3>
                    <p className="mt-1 text-navy-deep/80">{b.text}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust seals */}
            <div className="mt-10 flex flex-col items-center gap-6 border-t-2 border-navy-deep/10 pt-8 sm:flex-row sm:justify-center sm:gap-10">
              <a
                href="https://www.skincancer.org/early-detection/seal-of-recommendation/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Skin Cancer Foundation Seal of Recommendation — Effective Sun Protection for Outdoor Shade (opens in a new tab)"
                className="shrink-0 transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2"
              >
                <img
                  src={skinCancerSealAsset.url}
                  alt="The Skin Cancer Foundation Seal of Recommendation — Effective Sun Protection for Outdoor Shade"
                  className="h-28 w-auto md:h-32"
                  loading="lazy"
                />
              </a>
              <p className="max-w-md text-center text-sm text-navy-deep/80 sm:text-left">
                <strong className="font-display uppercase tracking-wider text-navy-deep">Skin Cancer Foundation — Seal of Recommendation.</strong>{" "}
                Sunbrella® shade fabrics we use on custom awnings are recognized as an effective UV protectant for outdoor shade.
              </p>
            </div>

            {/* Sunbrella video */}
            <div className="mt-12 overflow-hidden rounded-2xl border-2 border-navy-deep/10 bg-navy-deep shadow-xl">
              <div className="grid gap-0 md:grid-cols-5">
                <div className="md:col-span-3">
                  <video
                    src={sunbrellaVideoAsset.url}
                    className="h-full w-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    preload="metadata"
                    aria-label="Sunbrella outdoor fabric — built to live outside"
                  />
                </div>
                <div className="flex flex-col justify-center gap-3 p-6 text-white md:col-span-2 md:p-8">
                  <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Sunbrella®</div>
                  <h3 className="font-display text-2xl font-bold md:text-3xl">Built to Live Outside</h3>
                  <p className="text-white/85">
                    Every custom awning we build can be wrapped in genuine Sunbrella® canvas — solution-dyed, fade-resistant,
                    and backed by a 10-year fabric warranty. See why it's the fabric of choice for Texas sun.
                  </p>
                  <a
                    href="https://www.sunbrella.com/browse-fabrics/fabrics-by-use/shade-awnings-pergolas"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-red px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white shadow-lg transition hover:bg-red/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
                    aria-label="Shop the Sunbrella Showroom — Browse Shade, Awning & Pergola fabrics (opens in a new tab)"
                  >
                    Shop Sunbrella® Showroom — Browse Shade · Awning · Pergola Fabrics
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CUSTOM BENDING / SHOP CAPABILITY */}
        <section className="bg-cream py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-6">
            <div className="grid gap-10 md:grid-cols-2 md:items-center">
              <div className="overflow-hidden rounded-2xl border-2 border-navy-deep/10 shadow-xl">
                <img
                  src={customBenderAsset.url}
                  alt="Texas Made Shade in-house metal bending machine with pulley and chain drive used to custom-bend aluminum and steel awning frames"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div>
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Custom Bending In-House</div>
                <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
                  We Bend Metal. Other Awning Companies Only Sell What Comes in the Box.
                </h2>
                <p className="mt-4 text-lg text-navy-deep/80">
                  Most awning companies are stuck with one profile, one pitch, and one shape — whatever their supplier
                  drop-ships. We're not. Our Seguin shop runs a custom bending machine that lets us shape aluminum and
                  steel tubing to the exact curve, dome, radius, or angle your building calls for.
                </p>
                <p className="mt-4 text-lg text-navy-deep/80">
                  <strong className="text-navy-deep">We love a custom project.</strong>{" "}
                  Odd-angle storefront? Radius entry over a curved wall? A half-dome, a scallop, a barrel vault, a
                  compound-curve boat cover, or a one-of-a-kind residential window awning? Bring it. If you can sketch it
                  on a napkin, we can weld, bend, and cover it — no cookie-cutter kits, no "we don't do that."
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/estimate-quotes"
                    className="inline-flex items-center gap-2 rounded-full bg-navy-deep px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white shadow-lg transition hover:bg-navy-deep/90"
                  >
                    Start a Custom Project <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>



        {/* PROCESS STEPS */}
        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-6">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Our 5-Step Process</div>
            <h2 className="mt-2 max-w-3xl font-display text-4xl font-bold text-navy-deep md:text-5xl">
              How We Build Your Custom Awning
            </h2>
            <p className="mt-4 max-w-3xl text-lg text-navy-deep/80">
              From the first measurement to the final install, the same Texas crew handles every step. Here's what to expect.
            </p>
            <ol className="mt-10 grid list-none gap-px bg-navy-deep/10 p-0 md:grid-cols-2 lg:grid-cols-5">
              {STEPS.map((s, i) => (
                <li key={s.title} className="flex flex-col gap-3 bg-cream p-6">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center bg-navy-deep font-display text-sm font-bold text-white">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <s.icon className="h-6 w-6 text-red" />
                  </div>
                  <h3 className="font-display text-lg font-bold uppercase leading-tight text-navy-deep">
                    {s.title}
                  </h3>
                  <p className="text-sm text-navy-deep/80">{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Shop drawings / canopy awnings */}
        <section className="bg-cream py-16 md:py-20">
          <div className="mx-auto max-w-5xl px-6">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Shop Drawings & Sizing</div>
            <h2 className="mt-2 max-w-3xl font-display text-4xl font-bold text-navy-deep md:text-5xl">
              Canopy Awnings in San Antonio — Drawn Before They're Built
            </h2>
            <div className="mt-8 text-lg leading-relaxed text-navy-deep/85">
              <figure className="mb-6 w-full md:float-right md:mb-4 md:ml-8 md:w-[46%]">
                <img
                  src={canopyAwningsSanAntonio.url}
                  alt="Shop drawing quote for canopy awnings in San Antonio — a 144-inch by 144-inch white steel frame patio canopy with teal and cream striped fabric, 36-inch high back and 6-inch front drop over a poolside lounge chair"
                  width={1456}
                  height={1092}
                  loading="lazy"
                  decoding="async"
                  className="w-full border-2 border-navy-deep bg-white"
                />
                <figcaption className="bg-navy-deep px-4 py-3 text-sm text-white">
                  Canopy awnings in San Antonio — a to-scale quote drawing with real dimensions before a single piece of steel gets cut.
                </figcaption>
              </figure>
              <p>
                Every canopy awning we quote starts as a drawing like this one. Before you approve anything, you see the
                projection, the width, the height at the wall and the drop at the front edge, plus the stripe layout and
                color on the fabric — so there are no surprises the day our crew shows up with the frame.
              </p>
              <p className="mt-4">
                The example above is a freestanding 144&quot; x 144&quot; canopy: a welded steel frame finished in white,
                36&quot; of height at the high side sloping to a 6&quot; front fascia so water runs off instead of pooling,
                and a teal-and-cream striped top sewn in our Seguin shop. That same drawing tells us the post locations,
                the footing layout and the exact yardage of canvas to cut.
              </p>
              <p className="mt-4">
                Canopies like this go over pool decks, lounge areas, patios, walkways and entries all over San Antonio,
                New Braunfels, Seguin and the Hill Country. Send us your measurements or a photo of the space and we'll
                return a drawing and a firm number — no guessing, no stock-size compromises.
              </p>
              <p className="mt-6">
                <Link
                  to="/estimate-quotes"
                  className="inline-flex items-center gap-2 bg-red px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-red/90"
                >
                  Request a canopy awning drawing <ArrowRight className="h-4 w-4" />
                </Link>
              </p>
              <div className="clear-both" />
            </div>
          </div>
        </section>

        {/* FAQ */}

        <section className="bg-cream py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Common Questions</div>
            <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">Custom Awnings FAQ</h2>
            <div className="mt-8 divide-y divide-navy-deep/15 border-y border-navy-deep/15">
              {[
                { q: "How much do custom awnings cost in San Antonio?", a: "Most residential awnings run $1,800–$6,500 and storefront commercial awnings $4,000–$25,000+, depending on size, frame style and canvas. Send measurements and a photo and we'll come back with a written, line-item estimate same business day." },
                { q: "Do you build awnings in Austin and New Braunfels?", a: "Yes — Austin, New Braunfels, San Antonio, the Hill Country, the Texas Gulf Coast and Rio Grande Valley are core service areas. We pattern, fabricate in our Seguin shop and install with our own crew across Central and South Texas." },
                { q: "What canvas do you use and how long does it last?", a: "We use Sunbrella® and Tempotest® premium awning canvas — the same fabric specified on commercial storefronts nationwide. Expect 8–12 years of color and structural integrity in direct Texas sun before a re-cover, depending on exposure." },
                { q: "Can you re-cover my existing awning frame?", a: "Yes — re-covers are a major part of our work. If your welded aluminum or steel frame is sound, we pattern new canvas to it and re-skin it for 40–60% less than a brand-new install." },
                { q: "Do you handle custom shapes, signage and hand-painted logos?", a: "Yes — radius awnings, traditional shed and dome shapes, custom signage, screen-printed graphics and hand-painted logos are all in-house. See our Tuff Shed and Bradzoil case studies for examples." },
              ].map((f) => (
                <details key={f.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-display text-lg font-bold text-navy-deep">
                    {f.q}
                    <span className="font-display text-sm uppercase tracking-[0.2em] text-red group-open:hidden">+</span>
                    <span className="hidden font-display text-sm uppercase tracking-[0.2em] text-red group-open:inline">−</span>
                  </summary>
                  <p className="mt-3 text-base leading-relaxed text-navy-deep/80">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-navy-deep py-16 text-white md:py-20">
          <div className="mx-auto max-w-4xl px-6 text-center">
            
            <h2 className="mt-3 font-display text-3xl font-bold md:text-5xl">
              Get a free estimate on your custom awning.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-white/85">
              Send a few photos and rough measurements — we'll come back same business day with sizing questions and a free, no-pressure estimate.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                hash="lets-talk"
                className="inline-flex items-center gap-2 bg-red px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-red/90"
              >
                Get a Free Estimate <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="tel:+18303909588"
                className="inline-flex items-center gap-2 border-2 border-white px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
              >
                <Phone className="h-4 w-4" /> Call +1 (830) 390-9588
              </a>
            </div>
          </div>
        </section>

        <LetsTalkForm
          imageSrc={awningUnderneathAsset.url}
          imageAlt="Underside view of a large commercial awning frame showing the welded aluminum structure Texas Made Shade fabricates and recovers"
          extraFieldsSummary={[
            { name: "awningWidth", label: "How Wide" },
            { name: "awningHeight", label: "How Tall in Back" },
            { name: "awningProjection", label: "How Far Out From Wall" },
            { name: "awningSeries", label: "Series / Market", multiple: true },
            { name: "awningFabric", label: "Preferred Fabric" },
          ]}
          extraFields={<AwningExtras />}
        />


        <Lightbox
          images={GALLERY}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      </main>
      <PergolaUpgradeCta />
      <SiteFooter />
    </div>
  );
}
